import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URLS } from "../config/api";

const AdminLogin = () => {
  const [form, setForm] = useState({ 
    username: "", 
    password: "",
    rememberMe: false,
    captchaAnswer: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaResult, setCaptchaResult] = useState(0);
  const navigate = useNavigate();

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let captchaText = '';
    for (let i = 0; i < 8; i++) {
      captchaText += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaQuestion(captchaText);
    setCaptchaResult(captchaText);
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const tokenExpiry = localStorage.getItem("tokenExpiry");
    if (token && tokenExpiry) {
      const now = new Date().getTime();
      const expiryTime = parseInt(tokenExpiry);
      if (now < expiryTime) navigate("/admin/dashboard");
      else {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("tokenExpiry");
        localStorage.removeItem("rememberMe");
      }
    }
    generateCaptcha();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ 
      ...form, 
      [name]: type === 'checkbox' ? checked : value 
    });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (form.captchaAnswer.toUpperCase() !== captchaResult) {
      setError("CAPTCHA code is incorrect. Please try again.");
      setLoading(false);
      generateCaptcha();
      setForm({ ...form, captchaAnswer: "" });
      return;
    }

    try {
      const res = await axios.post(API_URLS.ADMIN_LOGIN, form, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data.success) {
        localStorage.setItem("adminToken", res.data.token);
        const expiryTime = form.rememberMe ? 
          new Date().getTime() + (7 * 24 * 60 * 60 * 1000) :
          new Date().getTime() + (2 * 60 * 60 * 1000);
        localStorage.setItem("tokenExpiry", expiryTime.toString());
        form.rememberMe ? localStorage.setItem("rememberMe", "true") : localStorage.removeItem("rememberMe");
        window.dispatchEvent(new Event("admin-login"));
        navigate("/admin/dashboard");
      } else setError(res.data.message || "Login failed");
    } catch (err) {
      // console.error("Login error:", err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors?.username || 
                          err.response?.data?.errors?.password ||
                          "Login failed. Please try again.";
      setError(errorMessage);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8" 
         style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      
      <div className="w-full max-w-md" style={{ transform: "scale(0.8)", transformOrigin: "top center" }}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Admin Login</h2>
            <h1 className="text-lg font-bold text-gray-700 mt-1">KABIR NAGAR KHEL SAMITI</h1>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-700 text-sm font-medium">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username Input */}
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-1">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none z-10">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="absolute inset-y-0 left-0 pl-8 flex items-center pointer-events-none z-5">
                  <div className="w-px h-6 bg-gray-300"></div>
                </div>
                <input
                  id="username"
                  type="text"
                  name="username"
                  placeholder="Enter your username"
                  value={form.username}
                  onChange={handleChange}
                  className="w-full pl-12 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 hover:bg-white text-gray-900 placeholder-gray-500"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="mt-6">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none z-10">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div className="absolute inset-y-0 left-0 pl-8 flex items-center pointer-events-none z-5">
                  <div className="w-px h-6 bg-gray-300"></div>
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 hover:bg-white text-gray-900 placeholder-gray-500"
                  required
                  disabled={loading}
                />
                {/* Password Eye Button */}
                <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
                  <div
                    onClick={() => setShowPassword(!showPassword)}
                    className="inline-block p-1 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="rememberMe"
                checked={form.rememberMe}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={loading}
              />
              <span className="ml-2 text-sm text-gray-700">Remember me</span>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Security Verification</label>
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0 px-3 py-2 bg-gray-50 border rounded-lg font-mono text-gray-800 tracking-widest font-bold select-none">{captchaQuestion}</div>
                <input
                  type="text"
                  name="captchaAnswer"
                  placeholder="Enter captcha"
                  value={form.captchaAnswer}
                  onChange={handleChange}
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-500 uppercase tracking-wider"
                  required
                  disabled={loading}
                  maxLength="8"
                />
              </div>
              {/* CAPTCHA Refresh Button - Redesigned */}
              <div className="mt-1 flex justify-start">
                  <div
                    onClick={generateCaptcha}
                    className="inline px-1 py-1 text-blue-600 hover:text-blue-700 transition-colors duration-200 cursor-pointer"
                    title="Refresh CAPTCHA"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition duration-200 disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="mt-4 text-center text-xs text-gray-500">
            Secure access to KNKSS Cricket Club administration
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;